/**
 * Super Admin Portal (Iteration 44) — deliberately isolated from the main app.
 *
 * All pages live in ONE file to keep the diff tight; each page is a small
 * component and the sidebar routes between them. Access is guarded by a
 * separate JWT stored under `ragnes_sa_token`.
 */
import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation, useParams,
} from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import {
  ShieldAlert, LayoutDashboard, Building2, Rocket, BriefcaseBusiness, BarChart3,
  History, LogOut, Plus, ArrowRight, Loader2, Eye, Ban, Play, Trash2, X, User, Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/superadmin`;
const LS_KEY = "ragnes_sa_token";

/* ---------- API helper ---------- */
async function sa(url, opts = {}) {
  const tok = localStorage.getItem(LS_KEY);
  const resp = await fetch(`${API}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const text = await resp.text();
  let body;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!resp.ok) {
    const msg = (body && (body.detail?.upgrade_message || body.detail || body.error)) || `HTTP ${resp.status}`;
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return body;
}

/* ---------- Guard ---------- */
function SaGuard({ children }) {
  const [state, setState] = useState({ loading: true, ok: false });
  useEffect(() => {
    const tok = localStorage.getItem(LS_KEY);
    if (!tok) { setState({ loading: false, ok: false }); return; }
    sa("/me").then(() => setState({ loading: false, ok: true }))
             .catch(() => { localStorage.removeItem(LS_KEY); setState({ loading: false, ok: false }); });
  }, []);
  if (state.loading) return <div className="flex h-screen items-center justify-center text-slate-400">Verifying…</div>;
  if (!state.ok) return <Navigate to="/superadmin/login" replace />;
  return children;
}

/* ---------- Layout ---------- */
function SaLayout({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const items = [
    { to: "/superadmin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/superadmin/workspaces", icon: Building2, label: "Workspaces" },
    { to: "/superadmin/early-access", icon: Rocket, label: "Early Access" },
    { to: "/superadmin/enterprise", icon: BriefcaseBusiness, label: "Enterprise" },
    { to: "/superadmin/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/superadmin/audit", icon: History, label: "Audit log" },
  ];
  return (
    <div className="flex h-screen w-full" style={{ background: "#0F172A", color: "#F1F5F9" }} data-testid="sa-layout">
      <aside className="w-64 shrink-0 border-r border-slate-800 flex flex-col" style={{ background: "#0B1220" }}>
        <div className="px-4 py-5 border-b border-slate-800 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-[10px] uppercase tracking-[0.28em] font-bold" style={{ background: "#DC2626", color: "#fff" }}>
            <ShieldAlert className="w-3 h-3" /> Ragnes Admin
          </span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((n) => {
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`sa-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded text-sm ${active ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`}>
                <n.icon className="w-4 h-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button data-testid="sa-logout"
            onClick={() => { localStorage.removeItem(LS_KEY); nav("/superadmin/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-xs text-slate-400 hover:text-white hover:bg-slate-800/60">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-8">{children}</main>
    </div>
  );
}

/* ---------- Login ---------- */
function SaLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await sa("/login", { method: "POST", body: JSON.stringify(form) });
      localStorage.setItem(LS_KEY, r.token);
      nav("/superadmin/dashboard");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#0B1220", color: "#F1F5F9" }}>
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl p-8 border border-slate-800" style={{ background: "#0F172A" }} data-testid="sa-login-form">
        <span className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-[10px] uppercase tracking-[0.28em] font-bold mb-6" style={{ background: "#DC2626", color: "#fff" }}>
          <ShieldAlert className="w-3 h-3" /> Ragnes Admin
        </span>
        <h1 className="text-2xl font-bold mb-1">Admin Portal</h1>
        <p className="text-sm text-slate-400 mb-6">Restricted access — super admins only.</p>
        <label className="text-xs uppercase tracking-wider text-slate-400">Email</label>
        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="sa-login-email"
          className="w-full mt-1 mb-4 h-11 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm outline-none focus:border-red-500" />
        <label className="text-xs uppercase tracking-wider text-slate-400">Password</label>
        <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="sa-login-password"
          className="w-full mt-1 mb-6 h-11 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm outline-none focus:border-red-500" />
        <button type="submit" disabled={busy} data-testid="sa-login-submit"
          className="w-full h-11 rounded-full text-sm font-medium text-white transition-transform hover:scale-[1.01]"
          style={{ background: "#DC2626" }}>
          {busy ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Sign in to Admin Portal"}
        </button>
      </form>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function SaDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [ea, setEa] = useState([]);
  useEffect(() => {
    sa("/analytics").then(setStats).catch((e) => toast.error(e.message));
    sa("/workspaces?limit=10").then((j) => setRecent((j.rows || []).slice(0, 10))).catch(() => {});
    sa("/early-access?status=pending").then(setEa).catch(() => {});
  }, []);
  return (
    <SaLayout>
      <h1 className="text-2xl font-bold mb-6" data-testid="sa-dashboard-heading">Platform Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="sa-dashboard-stats">
        {[
          { label: "Total Workspaces", value: stats?.total_workspaces ?? "—", tid: "total" },
          { label: "Active (paying)", value: stats?.total_active ?? "—", tid: "active" },
          { label: "On Trial", value: stats?.total_trial ?? "—", tid: "trial" },
          { label: "MRR (USD)", value: stats ? `$${stats.mrr_usd.toLocaleString()}` : "—", tid: "mrr" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-5 border border-slate-800" style={{ background: "#0B1220" }} data-testid={`sa-stat-${s.tid}`}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</p>
            <p className="text-3xl font-bold mt-2">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <section className="rounded-xl p-5 border border-slate-800" style={{ background: "#0B1220" }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Recent signups</h2>
            <Link to="/superadmin/workspaces" className="text-xs text-slate-400 hover:text-white">View all →</Link>
          </div>
          <ul className="space-y-2">
            {recent.map((w) => (
              <li key={w.workspace_id} className="flex items-center justify-between text-sm">
                <span className="truncate">{w.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{w.plan} · {w.status}</span>
              </li>
            ))}
            {recent.length === 0 && <li className="text-xs text-slate-500">No workspaces yet.</li>}
          </ul>
        </section>
        <section className="rounded-xl p-5 border border-slate-800" style={{ background: "#0B1220" }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Pending early access <span className="ml-2 rounded-full bg-amber-600/20 text-amber-300 px-2 text-xs">{ea.length}</span></h2>
            <Link to="/superadmin/early-access" className="text-xs text-slate-400 hover:text-white">Review →</Link>
          </div>
          <p className="text-xs text-slate-500">Platform queries this week: <b className="text-white">{stats?.total_queries_7d ?? "—"}</b></p>
        </section>
      </div>
    </SaLayout>
  );
}

/* ---------- Workspaces list + create ---------- */
function SaWorkspaces() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [preload, setPreload] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const LIMIT = 50;
  const load = () => sa(`/workspaces?page=${page}&limit=${LIMIT}${statusFilter !== "all" ? `&status=${statusFilter}` : ""}`)
    .then((j) => { setRows(j.rows || []); setTotal(j.total || 0); setHasMore(!!j.has_more); })
    .catch((e) => toast.error(e.message));
  // React 19 treats a Promise return from a useEffect body as a bad cleanup —
  // call load() inside so the effect body itself returns undefined.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, statusFilter]);
  // Reset to page 1 whenever the filter flips.
  useEffect(() => { setPage(1); }, [statusFilter]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (q && !`${r.name} ${r.admin_email || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q]);

  const [impersonateTarget, setImpersonateTarget] = useState(null); // { ws_id, ws_name }
  const impersonate = (w) => setImpersonateTarget({ ws_id: w.workspace_id, ws_name: w.name });

  const toggleSuspend = async (w) => {
    const path = w.status === "suspended" ? "reactivate" : "suspend";
    try { await sa(`/workspaces/${w.workspace_id}/${path}`, { method: "POST" }); toast.success(`Workspace ${path}d`); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <SaLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Workspaces</h1>
        <button onClick={() => { setPreload(null); setShowCreate(true); }}
          data-testid="sa-add-workspace"
          className="inline-flex items-center gap-2 rounded-full px-4 h-10 text-sm font-medium text-white" style={{ background: "#DC2626" }}>
          <Plus className="w-4 h-4" /> Add company
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sa-workspaces-search"
          placeholder="Search by company or admin email…"
          className="flex-1 min-w-[240px] h-10 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm outline-none focus:border-red-500" />
        {["all", "trial", "active", "suspended", "cancelled"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`sa-filter-${s}`}
            className={`h-10 px-3 rounded-full text-xs uppercase tracking-wider ${statusFilter === s ? "bg-red-600 text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden" style={{ background: "#0B1220" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-400">
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Seats</th>
              <th className="text-left p-3">Trial ends</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="sa-workspaces-tbody">
            {filtered.map((w) => (
              <tr key={w.workspace_id} className="border-t border-slate-800" data-testid={`sa-ws-row-${w.workspace_id}`}>
                <td className="p-3">
                  <Link to={`/superadmin/workspaces/${w.workspace_id}`} className="hover:underline">{w.name}</Link>
                  <div className="text-[11px] text-slate-500">{w.admin_email || "—"}</div>
                </td>
                <td className="p-3">
                  <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${w.plan === "growth" ? "bg-blue-600/20 text-blue-300" : w.plan === "enterprise" ? "bg-amber-600/20 text-amber-300" : "bg-slate-700 text-slate-300"}`}>{w.plan}</span>
                </td>
                <td className="p-3">
                  <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${w.status === "active" ? "bg-emerald-600/20 text-emerald-300" : w.status === "trial" ? "bg-amber-600/20 text-amber-300" : w.status === "suspended" ? "bg-red-600/20 text-red-300" : "bg-slate-700 text-slate-300"}`}>{w.status}</span>
                </td>
                <td className="p-3">{w.seats_used}{w.seats_limit != null ? ` / ${w.seats_limit}` : " / ∞"}</td>
                <td className="p-3 text-xs text-slate-400">{w.trial_ends_at ? new Date(w.trial_ends_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => impersonate(w)} data-testid={`sa-impersonate-${w.workspace_id}`}
                    className="text-xs text-slate-400 hover:text-white mr-3"><Eye className="w-3.5 h-3.5 inline mr-1" />Impersonate</button>
                  <button onClick={() => toggleSuspend(w)} data-testid={`sa-toggle-suspend-${w.workspace_id}`}
                    className="text-xs text-slate-400 hover:text-white">
                    {w.status === "suspended" ? <><Play className="w-3.5 h-3.5 inline mr-1" />Reactivate</> : <><Ban className="w-3.5 h-3.5 inline mr-1" />Suspend</>}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No workspaces match your filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <nav className="flex items-center justify-between mt-4 text-xs text-slate-400" data-testid="sa-workspaces-pagination">
        <span>
          Showing {rows.length ? (page - 1) * LIMIT + 1 : 0}–{(page - 1) * LIMIT + rows.length} of {total.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            data-testid="sa-workspaces-prev"
            className="px-3 py-1 rounded border border-slate-700 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
            ← Previous
          </button>
          <span className="font-mono">Page {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore}
            data-testid="sa-workspaces-next"
            className="px-3 py-1 rounded border border-slate-700 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
            Next →
          </button>
        </div>
      </nav>
      {showCreate && <CreateWorkspaceModal preload={preload} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {impersonateTarget && <ImpersonateReasonModal target={impersonateTarget} onClose={() => setImpersonateTarget(null)} />}
    </SaLayout>
  );
}

function ImpersonateReasonModal({ target, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) { toast.error("Enter a reason (min 3 chars)"); return; }
    setBusy(true);
    try {
      const r = await sa(`/workspaces/${target.ws_id}/impersonate`, {
        method: "POST", body: JSON.stringify({ reason: reason.trim() }),
      });
      localStorage.setItem("cb_token", r.token);
      window.location.href = "/app";
    } catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="sa-impersonate-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md rounded-2xl p-6 border border-slate-800" style={{ background: "#0F172A", color: "#F1F5F9" }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold">Impersonate {target.ws_name}</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          For the audit trail — enter a support ticket ID or a short reason. This will be visible on the workspace's impersonation timeline forever.
        </p>
        <label className="text-xs uppercase tracking-wider text-slate-400">Reason</label>
        <input required autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. SUPPORT-4821 · billing question"
          data-testid="sa-impersonate-reason-input"
          className="w-full mt-1 mb-6 h-11 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm outline-none focus:border-red-500" />
        <button type="submit" disabled={busy} data-testid="sa-impersonate-reason-submit"
          className="w-full h-11 rounded-full text-sm font-medium text-white" style={{ background: "#DC2626" }}>
          {busy ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Impersonate & go to workspace"}
        </button>
      </form>
    </div>
  );
}

function CreateWorkspaceModal({ preload, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: preload?.name || "", admin_email: preload?.email || "", admin_name: preload?.admin_name || "",
    plan: "starter", trial_days: 30, seats_limit: 10,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await sa("/workspaces", { method: "POST", body: JSON.stringify(form) });
      toast.success(`Workspace created. Invite sent to ${form.admin_email}.`);
      if (preload?.request_id) {
        await sa(`/early-access/${preload.request_id}/approve`, { method: "POST" }).catch(() => {});
      }
      onCreated(r);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full mt-1 h-10 rounded-md bg-slate-950 border border-slate-800 px-3 text-sm outline-none focus:border-red-500";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="sa-create-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md rounded-2xl p-6 border border-slate-800" style={{ background: "#0F172A", color: "#F1F5F9" }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold">Add new workspace</h2>
          <button type="button" onClick={onClose} data-testid="sa-create-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-400">Company name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="sa-create-name" /></div>
          <div><label className="text-xs text-slate-400">Admin full name</label>
            <input required value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className={inputCls} data-testid="sa-create-admin-name" /></div>
          <div><label className="text-xs text-slate-400">Admin email</label>
            <input required type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className={inputCls} data-testid="sa-create-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400">Plan</label>
              <select value={form.plan} onChange={(e) => { const p = e.target.value; setForm({ ...form, plan: p, seats_limit: p === "starter" ? 10 : p === "growth" ? 50 : 999 }); }} className={inputCls} data-testid="sa-create-plan">
                <option value="starter">Starter</option><option value="growth">Growth</option><option value="enterprise">Enterprise</option>
              </select></div>
            <div><label className="text-xs text-slate-400">Trial days</label>
              <input type="number" min="0" max="365" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: Number(e.target.value) })} className={inputCls} data-testid="sa-create-trial" /></div>
          </div>
          <div><label className="text-xs text-slate-400">Seat limit</label>
            <input type="number" min="1" value={form.seats_limit} onChange={(e) => setForm({ ...form, seats_limit: Number(e.target.value) })} className={inputCls} data-testid="sa-create-seats" /></div>
        </div>
        <button type="submit" disabled={busy} data-testid="sa-create-submit"
          className="w-full mt-6 h-11 rounded-full text-sm font-medium text-white" style={{ background: "#DC2626" }}>
          {busy ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Create & send invite"}
        </button>
      </form>
    </div>
  );
}

/* ---------- Workspace detail ---------- */
function SaWorkspaceDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [impersonations, setImpersonations] = useState([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const load = () => sa(`/workspaces/${id}`).then(setW).catch((e) => toast.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);
  useEffect(() => {
    sa(`/workspaces/${id}/impersonation-log`).then(setImpersonations).catch(() => setImpersonations([]));
  }, [id]);
  const impersonate = () => setShowReasonModal(true);
  if (!w) return <SaLayout><div className="text-slate-400">Loading…</div></SaLayout>;
  return (
    <SaLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{w.name}</h1>
          <p className="text-xs text-slate-400 mt-1">{w.workspace_id}</p>
        </div>
        <button onClick={impersonate} data-testid="sa-detail-impersonate"
          className="inline-flex items-center gap-2 rounded-full px-4 h-10 text-sm text-white" style={{ background: "#DC2626" }}>
          <Eye className="w-4 h-4" /> Impersonate admin →
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { l: "Plan", v: w.plan },
          { l: "Status", v: w.status },
          { l: "Seats", v: `${w.seats_used} / ${w.seats_limit ?? "∞"}` },
          { l: "Memory entries", v: w.memory_entry_count },
        ].map((s) => (
          <div key={s.l} className="rounded-xl p-5 border border-slate-800" style={{ background: "#0B1220" }}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.l}</p>
            <p className="text-2xl font-bold mt-2">{s.v}</p>
          </div>
        ))}
      </div>
      <section className="rounded-xl border border-slate-800 p-5 mb-6" style={{ background: "#0B1220" }}>
        <h2 className="text-sm font-semibold mb-3">Members ({w.members?.length || 0})</h2>
        <ul className="space-y-2">
          {(w.members || []).map((m) => (
            <li key={m.id} className="flex items-center justify-between text-sm border-b border-slate-800 py-2 last:border-none">
              <div><span className="font-medium">{m.name || m.email}</span><span className="text-xs text-slate-400 ml-2">{m.email}</span></div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{m.role}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-slate-800 p-5" style={{ background: "#0B1220" }} data-testid="sa-impersonation-timeline">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold">Impersonation history</h2>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">
            {impersonations.length} session{impersonations.length === 1 ? "" : "s"}
          </span>
        </div>
        {impersonations.length === 0 ? (
          <p className="text-xs text-slate-500 italic">
            No one has impersonated this workspace yet.
          </p>
        ) : (
          <ol className="space-y-3">
            {impersonations.map((row) => (
              <li key={row.id} className="flex items-start gap-3 text-sm" data-testid={`sa-impersonation-row-${row.id}`}>
                <div className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-red-400">{row.superadmin_email}</span>
                    <span className="text-xs text-slate-400">impersonated as</span>
                    <span className="font-mono text-xs text-slate-300">{row.extra?.as_user || "—"}</span>
                  </div>
                  {row.extra?.reason && (
                    <div className="text-xs text-slate-300 mt-1 italic" data-testid={`sa-impersonation-reason-${row.id}`}>
                      "{row.extra.reason}"
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-1">
                    {row.timestamp?.slice(0, 19).replace("T", " ")} UTC
                    {row.ip_address ? ` · IP ${row.ip_address}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
      {showReasonModal && (
        <ImpersonateReasonModal
          target={{ ws_id: id, ws_name: w.name }}
          onClose={() => setShowReasonModal(false)}
        />
      )}
    </SaLayout>
  );
}

/* ---------- Early access ---------- */
function SaEarlyAccess() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [preload, setPreload] = useState(null);
  const load = () => sa(`/early-access?status=${tab}`).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  const reject = async (id) => { try { await sa(`/early-access/${id}/reject`, { method: "POST" }); toast.success("Rejected"); load(); } catch (e) { toast.error(e.message); } };
  return (
    <SaLayout>
      <h1 className="text-2xl font-bold mb-6">Early Access</h1>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`sa-ea-tab-${s}`}
            className={`h-10 px-4 rounded-full text-xs uppercase tracking-wider ${tab === s ? "bg-red-600 text-white" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-800 overflow-hidden" style={{ background: "#0B1220" }}>
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
            <th className="text-left p-3">Name</th><th className="text-left p-3">Company</th>
            <th className="text-left p-3">Industry</th><th className="text-left p-3">Team</th>
            <th className="text-left p-3">Submitted</th><th className="text-left p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800">
                <td className="p-3">{r.name}<div className="text-[11px] text-slate-500">{r.email}</div></td>
                <td className="p-3">{r.company || "—"}</td>
                <td className="p-3 text-xs">{r.industry || "—"}</td>
                <td className="p-3 text-xs">{r.company_size || "—"}</td>
                <td className="p-3 text-xs text-slate-400">{r.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-xs">{r.status || "pending"}</td>
                <td className="p-3 text-right">
                  {(r.status || "pending") === "pending" && (
                    <>
                      <button onClick={() => setPreload({ name: r.company || r.name, email: r.email, admin_name: r.name, request_id: r.id })}
                        data-testid={`sa-ea-approve-${r.id}`}
                        className="text-xs text-emerald-400 hover:text-emerald-300 mr-3">Approve →</button>
                      <button onClick={() => reject(r.id)} data-testid={`sa-ea-reject-${r.id}`}
                        className="text-xs text-red-400 hover:text-red-300">Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No requests.</td></tr>}
          </tbody>
        </table>
      </div>
      {preload && <CreateWorkspaceModal preload={preload} onClose={() => setPreload(null)} onCreated={() => { setPreload(null); load(); }} />}
    </SaLayout>
  );
}

/* ---------- Enterprise enquiries ---------- */
function SaEnterprise() {
  const [rows, setRows] = useState([]);
  const load = () => sa("/enterprise-enquiries").then(setRows).catch(() => setRows([]));
  useEffect(load, []);
  const update = async (id, patch) => { try { await sa(`/enterprise-enquiries/${id}`, { method: "PUT", body: JSON.stringify(patch) }); load(); } catch (e) { toast.error(e.message); } };
  return (
    <SaLayout>
      <h1 className="text-2xl font-bold mb-6">Enterprise Enquiries</h1>
      <div className="rounded-xl border border-slate-800 overflow-hidden" style={{ background: "#0B1220" }}>
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
            <th className="text-left p-3">Name</th><th className="text-left p-3">Company</th>
            <th className="text-left p-3">Size</th><th className="text-left p-3">Message</th>
            <th className="text-left p-3">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800">
                <td className="p-3">{r.name}<div className="text-[11px] text-slate-500">{r.email}</div></td>
                <td className="p-3">{r.company}</td>
                <td className="p-3 text-xs">{r.size}</td>
                <td className="p-3 text-xs text-slate-400 max-w-xs truncate">{r.message || "—"}</td>
                <td className="p-3">
                  <select value={r.status || "new"} onChange={(e) => update(r.id, { status: e.target.value })}
                    className="text-xs bg-slate-950 border border-slate-800 rounded px-2 py-1">
                    {["new", "contacted", "qualified", "closed_won", "closed_lost"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No enquiries.</td></tr>}
          </tbody>
        </table>
      </div>
    </SaLayout>
  );
}

/* ---------- Analytics ---------- */
function SaAnalytics() {
  const [d, setD] = useState(null);
  const [ts, setTs] = useState(null);
  useEffect(() => {
    sa("/analytics").then(setD).catch(() => {});
    sa("/analytics/timeseries?months=12").then(setTs).catch(() => {});
  }, []);
  if (!d) return <SaLayout><div className="text-slate-400">Loading…</div></SaLayout>;
  const pieData = Object.entries(d.plan_distribution).map(([name, value]) => ({ name, value }));
  const pieColors = { starter: "#64748B", growth: "#2563EB", enterprise: "#F59E0B" };
  const trend = ts?.months || [];
  const chartAxis = { stroke: "#475569", fontSize: 11 };
  const tooltipStyle = { background: "#0B1220", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12, color: "#F1F5F9" };
  return (
    <SaLayout>
      <h1 className="text-2xl font-bold mb-6" data-testid="sa-analytics-heading">Analytics</h1>

      {/* Compact stat strip — kept as a scannable summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { l: "MRR (USD)", v: `$${d.mrr_usd.toLocaleString()}` },
          { l: "ARR (USD)", v: `$${d.arr_usd.toLocaleString()}` },
          { l: "New / 30d", v: d.new_workspaces_30d },
          { l: "Churned / 30d", v: d.churned_30d },
        ].map((s) => (
          <div key={s.l} className="rounded-lg p-4 border border-slate-800" style={{ background: "#0B1220" }}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.l}</p>
            <p className="text-xl font-bold mt-1">{s.v}</p>
          </div>
        ))}
      </div>

      {/* MRR trend — stacked cohort area: existing (base) + new (this month) */}
      <section className="rounded-xl border border-slate-800 p-5 mb-6" style={{ background: "#0B1220" }} data-testid="sa-chart-mrr">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">MRR trend · last 12 months</h2>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">Cohort split — expansion revenue jumps out in green</p>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-emerald-400">
            ${d.mrr_usd.toLocaleString()} current
          </span>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={trend} margin={{ top: 10, right: 20, bottom: 4, left: 0 }}>
              <defs>
                <linearGradient id="mrrExisting" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="mrrNew" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="month" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              <Area type="monotone" name="Existing MRR" dataKey="mrr_existing_usd" stackId="1"
                stroke="#10B981" fill="url(#mrrExisting)" strokeWidth={2} />
              <Area type="monotone" name="New MRR (cohort)" dataKey="mrr_new_usd" stackId="1"
                stroke="#38BDF8" fill="url(#mrrNew)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* New workspaces bar */}
        <section className="rounded-xl border border-slate-800 p-5" style={{ background: "#0B1220" }} data-testid="sa-chart-new-workspaces">
          <h2 className="text-sm font-semibold mb-3">New workspaces / month</h2>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 10, right: 10, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="month" {...chartAxis} />
                <YAxis {...chartAxis} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="new_workspaces" fill="#38BDF8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="churned" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Plan distribution pie */}
        <section className="rounded-xl border border-slate-800 p-5" style={{ background: "#0B1220" }} data-testid="sa-chart-plans">
          <h2 className="text-sm font-semibold mb-3">Plan distribution</h2>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={pieColors[entry.name] || "#64748B"} stroke="#0B1220" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { l: "Total seats", v: d.total_seats },
          { l: "Avg memory / ws", v: d.avg_memory_entries_per_workspace },
          { l: "Queries / 7d", v: d.total_queries_7d },
          { l: "Active", v: d.total_active },
        ].map((s) => (
          <div key={s.l} className="rounded-lg p-4 border border-slate-800" style={{ background: "#0B1220" }}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.l}</p>
            <p className="text-xl font-bold mt-1">{s.v}</p>
          </div>
        ))}
      </div>
    </SaLayout>
  );
}

/* ---------- Audit log ---------- */
function SaAudit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { sa("/audit").then(setRows).catch(() => {}); }, []);
  return (
    <SaLayout>
      <h1 className="text-2xl font-bold mb-6">Audit log</h1>
      <div className="rounded-xl border border-slate-800 overflow-hidden" style={{ background: "#0B1220" }}>
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
            <th className="text-left p-3">Time</th><th className="text-left p-3">Admin</th>
            <th className="text-left p-3">Action</th><th className="text-left p-3">Target</th>
            <th className="text-left p-3">IP</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800">
                <td className="p-3 text-xs">{r.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="p-3 text-xs">{r.superadmin_email}</td>
                <td className="p-3 text-xs font-mono">{r.action}</td>
                <td className="p-3 text-xs text-slate-400 font-mono">{r.target_id || "—"}</td>
                <td className="p-3 text-xs text-slate-500">{r.ip_address || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SaLayout>
  );
}

/* ---------- Router entry ---------- */
export default function SuperAdminRoutes() {
  return (
    <Routes>
      <Route path="login" element={<SaLogin />} />
      <Route path="dashboard" element={<SaGuard><SaDashboard /></SaGuard>} />
      <Route path="workspaces" element={<SaGuard><SaWorkspaces /></SaGuard>} />
      <Route path="workspaces/:id" element={<SaGuard><SaWorkspaceDetail /></SaGuard>} />
      <Route path="early-access" element={<SaGuard><SaEarlyAccess /></SaGuard>} />
      <Route path="enterprise" element={<SaGuard><SaEnterprise /></SaGuard>} />
      <Route path="analytics" element={<SaGuard><SaAnalytics /></SaGuard>} />
      <Route path="audit" element={<SaGuard><SaAudit /></SaGuard>} />
      <Route path="*" element={<Navigate to="/superadmin/dashboard" replace />} />
    </Routes>
  );
}
